import { RomanticPhrase } from './infinitePhrases';
import {
  ALL_ROMANTIC_FONTS,
  FONTS_BY_ID,
  ensureGoogleFontLoaded,
  RomanticFontItem,
} from './romanticFontsCatalog';

export type NoteFontFamily = string;

export interface NoteFontOption {
  id: NoteFontFamily;
  name: string;
  category: string;
  fontFamily: string;
  previewClass: string;
}

export const NOTE_FONT_OPTIONS: NoteFontOption[] = ALL_ROMANTIC_FONTS.slice(0, 12).map((f) => ({
  id: f.id,
  name: f.name,
  category: f.categoryLabel,
  fontFamily: f.cssFamily,
  previewClass: `text-xs font-semibold`,
}));

export interface FontConfig {
  style: string;
  weight: number | string;
  family: string;
  baseSize: number;
  baseLineHeight: number;
  inkColor: string;
  authorStyle: string;
  authorWeight: number | string;
  authorSize: number;
  authorFamily: string;
}

export const FONT_CONFIGS: Record<string, FontConfig> = {
  caveat: {
    style: 'normal',
    weight: 700,
    family: "Caveat, cursive, sans-serif",
    baseSize: 96,
    baseLineHeight: 126,
    inkColor: '#0b101d',
    authorStyle: 'normal',
    authorWeight: 700,
    authorSize: 44,
    authorFamily: "Caveat, cursive, sans-serif",
  },
  dancing: {
    style: 'normal',
    weight: 700,
    family: "Dancing Script, cursive, serif",
    baseSize: 86,
    baseLineHeight: 120,
    inkColor: '#0b101d',
    authorStyle: 'normal',
    authorWeight: 700,
    authorSize: 42,
    authorFamily: "Dancing Script, cursive, serif",
  },
  playfair: {
    style: 'italic',
    weight: 700,
    family: "Playfair Display, Georgia, serif",
    baseSize: 76,
    baseLineHeight: 114,
    inkColor: '#111827',
    authorStyle: 'normal',
    authorWeight: 600,
    authorSize: 38,
    authorFamily: "Playfair Display, Georgia, serif",
  },
  cormorant: {
    style: 'italic',
    weight: 700,
    family: "Cormorant Garamond, Georgia, serif",
    baseSize: 88,
    baseLineHeight: 118,
    inkColor: '#111827',
    authorStyle: 'italic',
    authorWeight: 600,
    authorSize: 42,
    authorFamily: "Cormorant Garamond, Georgia, serif",
  },
  courier: {
    style: 'normal',
    weight: 700,
    family: "Courier Prime, Courier, monospace",
    baseSize: 66,
    baseLineHeight: 98,
    inkColor: '#1e293b',
    authorStyle: 'normal',
    authorWeight: 700,
    authorSize: 34,
    authorFamily: "Courier Prime, Courier, monospace",
  },
  jakarta: {
    style: 'normal',
    weight: 600,
    family: "Plus Jakarta Sans, sans-serif",
    baseSize: 70,
    baseLineHeight: 104,
    inkColor: '#0b101d',
    authorStyle: 'normal',
    authorWeight: 600,
    authorSize: 36,
    authorFamily: "Plus Jakarta Sans, sans-serif",
  },
};

export function getFontConfig(fontId: string): FontConfig {
  const font = FONTS_BY_ID[fontId];
  if (font) {
    return {
      style: font.style || 'normal',
      weight: font.weight || 600,
      family: font.cssFamily,
      baseSize: font.baseSize || 76,
      baseLineHeight: font.baseLineHeight || 114,
      inkColor: font.inkColor || '#0b101d',
      authorStyle: font.style === 'italic' ? 'italic' : 'normal',
      authorWeight: 600,
      authorSize: Math.round((font.baseSize || 76) * 0.48),
      authorFamily: font.cssFamily,
    };
  }
  return FONT_CONFIGS[fontId] || FONT_CONFIGS.caveat;
}

/**
 * Construye una cadena válida de fuente para Canvas 2D según la especificación W3C:
 * [font-style] [font-weight] [font-size]px [font-family]
 */
function formatCanvasFont(
  style: string,
  weight: string | number,
  sizePx: number,
  family: string
): string {
  const parts: string[] = [];
  if (style && style !== 'normal') {
    parts.push(style);
  }
  if (weight) {
    parts.push(String(weight));
  }
  parts.push(`${Math.round(sizePx)}px`);
  // Eliminar comillas internas para máxima compatibilidad con todos los motores Canvas
  const cleanFamily = family.replace(/['"]/g, '');
  parts.push(cleanFamily);
  return parts.join(' ');
}

export interface NoteImageResult {
  dataUrl: string;
  blob: Blob;
  fileName: string;
}

/**
 * Genera una imagen estética de alta resolución (1080x1350) estilo HOJA DE CUADERNO / LIBRETA PREMIUM.
 * Mejoras aplicadas:
 * 1. Hoja de libreta realista con esquinas redondeadas, sombra 3D multicapa sobre fondo fotográfico suave.
 * 2. Doble línea de margen en rosa/carmesí sutil y pautado horizontal nítido y balanceado.
 * 3. Tipografía grande, audaz y con contraste de tinta real (fountain pen ink), centrada y proporcionada.
 * 4. Solamente la frase y su autor (sin marcas ni logos molestos).
 */
export async function generateNoteCardImage(
  phrase: RomanticPhrase,
  fontId: NoteFontFamily = 'caveat',
  phraseNumber?: number
): Promise<NoteImageResult> {
  // Asegurar carga de la fuente seleccionada
  await ensureGoogleFontLoaded(fontId);
  const fontConfig = getFontConfig(fontId);

  // Asegurar que las fuentes web estén listas y cargadas en memoria del navegador
  if (typeof document !== 'undefined' && document.fonts) {
    try {
      await document.fonts.ready;
      const testString = formatCanvasFont(
        fontConfig.style,
        fontConfig.weight,
        48,
        fontConfig.family
      );
      await document.fonts.load(testString);
    } catch {
      // Continuar con fallback si el navegador no soporta load
    }
  }

  const width = 1080;
  const height = 1350;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No se pudo inicializar el contexto 2D de Canvas');
  }

  // 1. Hoja completa de cuaderno de alta calidad (100% full-bleed sin bordes oscuros)
  // Base de degradado cálido de papel de algodón fino (ivory / marfil natural de papelería)
  const paperGrad = ctx.createLinearGradient(0, 0, width, height);
  paperGrad.addColorStop(0, '#fdfcf7');
  paperGrad.addColorStop(0.25, '#faf7ef');
  paperGrad.addColorStop(0.65, '#f7f2e6');
  paperGrad.addColorStop(1, '#f3ece0');
  ctx.fillStyle = paperGrad;
  ctx.fillRect(0, 0, width, height);

  // Iluminación cenital natural sobre la hoja
  const naturalLight = ctx.createLinearGradient(0, 0, width, height * 0.7);
  naturalLight.addColorStop(0, 'rgba(255, 255, 255, 0.45)');
  naturalLight.addColorStop(0.35, 'rgba(255, 255, 255, 0.15)');
  naturalLight.addColorStop(1, 'transparent');
  ctx.fillStyle = naturalLight;
  ctx.fillRect(0, 0, width, height);

  // Textura de papel de alta calidad: micro-grano de celulosa y fibras orgánicas de algodón
  renderHighQualityPaperTexture(ctx, 0, 0, width, height);

  // 2. Encabezado sutil de cuaderno clásico
  ctx.save();
  ctx.font = '600 19px Plus Jakarta Sans, sans-serif';
  ctx.fillStyle = 'rgba(120, 135, 155, 0.40)';
  ctx.letterSpacing = '2px';
  ctx.fillText('FECHA:  ____ / ____ / 20__', width - 360, 72);
  ctx.restore();

  // 3. Línea vertical de margen de cuaderno (rojo/carmesí clásico de libreta)
  const marginLineX = 138;
  ctx.beginPath();
  ctx.moveTo(marginLineX, 0);
  ctx.lineTo(marginLineX, height);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(244, 63, 94, 0.35)'; // Rojo de margen tenue
  ctx.stroke();

  // Línea secundaria de margen (doble línea clásica de cuaderno)
  ctx.beginPath();
  ctx.moveTo(marginLineX + 5, 0);
  ctx.lineTo(marginLineX + 5, height);
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(244, 63, 94, 0.18)';
  ctx.stroke();

  // 4. Líneas horizontales pautadas de libreta (rayado azul pizarra suave que recorre toda la hoja al 100%)
  const lineSpacing = 70;
  const startLineY = 120;
  const endLineY = height - 40;

  ctx.lineWidth = 1.15;
  ctx.strokeStyle = 'rgba(96, 125, 162, 0.22)'; // Azul pauta tenue

  for (let y = startLineY; y <= endLineY; y += lineSpacing) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  // 5. Configuración tipográfica con tamaño grande, legible y efecto de tinta real escrita a mano
  const rawText = phrase.text.trim();
  const textWithQuotes = `“${rawText}”`;

  // Área útil de escritura amplia y centrada en la hoja
  const textLeftMargin = marginLineX + 42;
  const textRightMargin = width - 65;
  const maxTextWidth = textRightMargin - textLeftMargin;
  const textCenterX = textLeftMargin + maxTextWidth / 2;

  // Ajuste inteligente según longitud de la frase para máximo protagonismo
  let fontSize = fontConfig.baseSize;
  let lineHeight = fontConfig.baseLineHeight;

  if (rawText.length < 55) {
    fontSize = Math.round(fontConfig.baseSize * 1.25);
    lineHeight = Math.round(fontConfig.baseLineHeight * 1.25);
  } else if (rawText.length < 110) {
    fontSize = Math.round(fontConfig.baseSize * 1.12);
    lineHeight = Math.round(fontConfig.baseLineHeight * 1.12);
  } else if (rawText.length > 220) {
    fontSize = Math.round(fontConfig.baseSize * 0.82);
    lineHeight = Math.round(fontConfig.baseLineHeight * 0.84);
  } else if (rawText.length > 160) {
    fontSize = Math.round(fontConfig.baseSize * 0.90);
    lineHeight = Math.round(fontConfig.baseLineHeight * 0.92);
  }

  ctx.font = formatCanvasFont(fontConfig.style, fontConfig.weight, fontSize, fontConfig.family);
  let lines = getWrappedLines(ctx, textWithQuotes, maxTextWidth);
  const maxAllowedHeight = height * 0.72;

  while (
    lines.length * lineHeight + (phrase.author ? 85 : 0) > maxAllowedHeight &&
    fontSize > 38
  ) {
    fontSize -= 3;
    lineHeight = Math.round(fontSize * (fontConfig.baseLineHeight / fontConfig.baseSize));
    ctx.font = formatCanvasFont(fontConfig.style, fontConfig.weight, fontSize, fontConfig.family);
    lines = getWrappedLines(ctx, textWithQuotes, maxTextWidth);
  }

  // Centrado vertical armónico en la página
  const totalTextHeight = lines.length * lineHeight;
  const authorSpacing = phrase.author ? 85 : 0;
  const totalBlockHeight = totalTextHeight + authorSpacing;

  let currentY = (height - totalBlockHeight) / 2 + lineHeight * 0.72;

  // 6. Plasmar la frase con efecto de tinta manuscrita real (capilaridad + ligera variación orgánica)
  ctx.save();
  ctx.font = formatCanvasFont(fontConfig.style, fontConfig.weight, fontSize, fontConfig.family);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Capa 1: Capilaridad y absorción de tinta en fibras de papel (micro-sombra de tinta húmeda)
  ctx.shadowColor = 'rgba(14, 20, 35, 0.32)';
  ctx.shadowBlur = 1.6;
  ctx.shadowOffsetX = 0.25;
  ctx.shadowOffsetY = 0.35;

  lines.forEach((line, idx) => {
    ctx.save();
    // Micro-variación humana del pulso en cada línea (sutil ángulo de ±0.22 grados para dar realismo artesanal)
    const microTilt = (idx % 2 === 0 ? 0.003 : -0.0025);
    ctx.translate(textCenterX, currentY);
    ctx.rotate(microTilt);

    // Primera pasada: cuerpo de tinta con absorción
    ctx.fillStyle = fontConfig.inkColor;
    ctx.fillText(line, 0, 0);

    // Segunda pasada: núcleo denso de pluma estilográfica / tinta líquida
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = 'rgba(10, 14, 24, 0.45)';
    ctx.fillText(line, 0, 0);

    ctx.restore();
    currentY += lineHeight;
  });

  ctx.restore();

  // 7. Si tiene autor, plasmarlo como firma manuscrita elegante
  if (phrase.author) {
    const authorY = currentY + 38;
    ctx.save();
    ctx.font = formatCanvasFont(
      fontConfig.authorStyle,
      fontConfig.authorWeight,
      fontConfig.authorSize,
      fontConfig.authorFamily
    );
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Sombra de tinta sutil para la firma
    ctx.shadowColor = 'rgba(15, 23, 42, 0.25)';
    ctx.shadowBlur = 1.2;
    ctx.shadowOffsetX = 0.2;
    ctx.shadowOffsetY = 0.2;

    ctx.fillStyle = '#2d3748'; // Tinta carbón suave de firma
    ctx.fillText(`—  ${phrase.author}  —`, textCenterX, authorY);
    ctx.restore();
  }

  // 8. Generar DataURL y Blob para descarga y compartir (100% full-bleed sin bordes oscuros)
  const dataUrl = canvas.toDataURL('image/png', 0.96);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('No se pudo generar el archivo Blob de la imagen'));
      },
      'image/png',
      0.96
    );
  });

  const numberSuffix = phraseNumber && phraseNumber > 0 ? phraseNumber : 1;
  const fileName = `Frases #${numberSuffix}.png`;

  return {
    dataUrl,
    blob,
    fileName,
  };
}

// Dibuja un rectángulo con esquinas redondeadas
function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

// Auxiliar para envolver texto calculando ancho exacto por métricas del lienzo
function getWrappedLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && i > 0) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Genera una textura procedimental de papel de algodón de alta calidad (120 gsm).
 * Simula micro-fibras orgánicas de celulosa y grano fino de prensado en frío para un realismo auténtico.
 */
function renderHighQualityPaperTexture(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  try {
    const patternCanvas = document.createElement('canvas');
    const pSize = 256;
    patternCanvas.width = pSize;
    patternCanvas.height = pSize;
    const pCtx = patternCanvas.getContext('2d');
    if (!pCtx) return;

    // 1. Grano fino de algodón con micro-variaciones de temperatura cálida marfil
    const imgData = pCtx.createImageData(pSize, pSize);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 24;
      const base = 128 + noise;
      data[i] = Math.min(255, Math.max(0, base + 2)); // R (tono cálido)
      data[i + 1] = Math.min(255, Math.max(0, base));     // G
      data[i + 2] = Math.min(255, Math.max(0, base - 3)); // B (marfil suave)
      data[i + 3] = 255;
    }
    pCtx.putImageData(imgData, 0, 0);

    // 2. Micro-fibras orgánicas de papel esparcidas delicadamente
    pCtx.lineWidth = 0.75;
    for (let f = 0; f < 55; f++) {
      const fx = Math.random() * pSize;
      const fy = Math.random() * pSize;
      const len = 3 + Math.random() * 8;
      const angle = Math.random() * Math.PI * 2;
      const curl = (Math.random() - 0.5) * 3;

      pCtx.strokeStyle =
        Math.random() > 0.4
          ? 'rgba(140, 120, 95, 0.16)'
          : 'rgba(255, 255, 255, 0.25)';

      pCtx.beginPath();
      pCtx.moveTo(fx, fy);
      pCtx.quadraticCurveTo(
        fx + Math.cos(angle) * (len / 2) + curl,
        fy + Math.sin(angle) * (len / 2) + curl,
        fx + Math.cos(angle) * len,
        fy + Math.sin(angle) * len
      );
      pCtx.stroke();
    }

    // 3. Fibras delgadas oscuras ocasionales (celulosa vegetal de papel de libreta fino)
    for (let f = 0; f < 10; f++) {
      const fx = Math.random() * pSize;
      const fy = Math.random() * pSize;
      pCtx.strokeStyle = 'rgba(115, 90, 65, 0.2)';
      pCtx.lineWidth = 0.85;
      pCtx.beginPath();
      pCtx.moveTo(fx, fy);
      pCtx.lineTo(fx + (Math.random() - 0.5) * 5, fy + (Math.random() - 0.5) * 5);
      pCtx.stroke();
    }

    const pattern = ctx.createPattern(patternCanvas, 'repeat');
    if (pattern) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = 0.14; // Balance óptimo para nitidez y textura táctil
      ctx.fillStyle = pattern;
      ctx.fillRect(x, y, width, height);
      ctx.restore();
    }
  } catch (err) {
    console.warn('Fallback sin textura procedural', err);
  }
}

